import React, { Component } from 'react';
import { FormicaProps, withFormica } from '../../../../helpers/withFormica';
import { RootState } from '../../../../redux/store';
import { AiModelState } from '../../../../redux/reducers/AiModelReducer';
import { changeAiModel } from '../../../../redux/actions/AiModelActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import CustomTable from '../../../custom/CustomTable';
import { ColumnProps } from 'primereact/column';
import { customConfirm } from '../../../custom/CustomPopupConfirm';
import LanguageUtil from '../../../../util/LanguageUtil';
import ComponentName from '../../../../constant/ComponentName';
import { Tooltip } from 'primereact/tooltip';
import Icon from '../../../../icons/Icon';
import DataType from 'constant/DataType';
import { Syntax, VariableToken } from '@trlogic/ui-pack';
import VariableType from 'constant/VariableType';
import MessageUtil from '../../../../util/MessageUtil';
import { InputSwitch } from 'primereact/inputswitch';
import RoleHelper from '../../../../RoleHelper';
import CustomInput from '../../../custom/CustomInput';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../../custom/FormicaSearchSelectBox';
import { MLOpsModelBaseFeature } from '../../../../domain/mlops/MLOpsDomains';
import EmptyListComponent from '../../../custom/empty-components/EmptyListComponent';
import FilterUtil from '../../../../util/FilterUtil';

class AiModelOriginalFeaturesComponent extends Component<Props> {
  public render() {
    const {
      aiModel: { aiModel },
    } = this.props;
    if (!aiModel.baseFeatures || aiModel.baseFeatures?.length <= 0) {
      return <EmptyListComponent />;
    }
    return <CustomTable columns={this.getColumns()} value={aiModel.baseFeatures} />;
  }

  private getColumns = (): ColumnProps[] => {
    return [
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.ai.editor.features.baseFeatures.table.name', defaultMessage: 'Name' }),
        className: 'min-w-[20%]',
        columnKey: 'name',
      },
      {
        field: 'type',
        header: LanguageUtil.get({ id: 'pages.ai.editor.features.baseFeatures.table.type', defaultMessage: 'Type' }),
        className: 'min-w-[20%]',
        columnKey: 'type',
        body: (rowData: MLOpsModelBaseFeature) => {
          return (
            <FormicaSearchSelectBox
              rounded
              ghost
              options={this.getDataTypeOptions()}
              value={rowData.properties.dataType}
              onChange={(e) => this.onChangeDataType(e, rowData)}
            />
          );
        },
      },
      {
        field: 'included',
        header: LanguageUtil.get({ id: 'pages.ai.editor.features.baseFeatures.table.included', defaultMessage: 'Included' }),
        className: 'min-w-[20%]',
        columnKey: 'included',
        body: (rowData: MLOpsModelBaseFeature) => {
          return <InputSwitch checked={rowData.enabled} onChange={(e) => this.onChangeEnabled(e.value, rowData)} />;
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'pages.ai.editor.features.baseFeatures.table.description', defaultMessage: 'Description' }),
        className: 'min-w-[30%]',
        columnKey: 'description',
        body: (rowData: MLOpsModelBaseFeature) => {
          return (
            <CustomInput
              inputType={'ghost'}
              disabled={!RoleHelper.canEdit(ComponentName.AI_MODEL)}
              onChange={(e) => this.onChangeDescription(e, rowData)}
              value={rowData.description}
            />
          );
        },
      },
      {
        field: 'action',
        header: LanguageUtil.get({ id: 'pages.ai.editor.features.baseFeatures.table.action', defaultMessage: 'Action' }),
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        body: (rowData: MLOpsModelBaseFeature) => {
          return (
            <div
              key='remove'
              onClick={(e: any) => {
                e.stopPropagation();
                e.preventDefault();
                customConfirm({
                  target: e.currentTarget,
                  accept: () => this.remove(rowData),
                  message: LanguageUtil.get({ id: 'messages.confirm.confirmRemove', defaultMessage: 'Are you sure you want to remove?' }),
                });
              }}
              data-testid={`${ComponentName.AI_MODEL}_remove_button`}
            >
              <Tooltip position={'left'} target={'.remove'}>
                {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
              </Tooltip>
              <Icon
                icon={'delete'}
                size={16}
                color={'primary'}
                className={'remove hover:text-primary text-f-black-4 hover:cursor-pointer flex items-center justify-center'}
              />
            </div>
          );
        },
      },
    ];
  };

  private remove = async (rowData: MLOpsModelBaseFeature) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
      incrementUpdate,
    } = this.props;
    const isUsed = this.isUsingInCustomFeatures(rowData);
    if (isUsed.length > 0) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.error', defaultMessage: 'Error' }),
        detail: LanguageUtil.get(
          {
            id: 'messages.error.usedCustomFeatures',
            defaultMessage: `This feature is used in custom features: ${isUsed.join(', ')}`,
          },
          { 0: `${isUsed.join(', ')}` },
        ),
      });
      return;
    }
    const index = aiModel.baseFeatures.indexOf(rowData);
    if (index != -1) {
      aiModel.baseFeatures.splice(index, 1);
      changeAiModel(aiModel);
      await FilterUtil.getMLOpsDatasourceFields(aiModel.channel, aiModel.baseFeatures, 'TRAINING', true);
      await FilterUtil.getMLOpsDatasourceFields(aiModel.channel, aiModel.baseFeatures, 'VALIDATION', true);
      incrementUpdate();
    }
  };

  private onChangeEnabled = (e: boolean, rowData: MLOpsModelBaseFeature) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    rowData.enabled = e;
    changeAiModel(aiModel);
  };

  private isUsingInCustomFeatures = (rowData: MLOpsModelBaseFeature) => {
    const {
      aiModel: { aiModel },
    } = this.props;
    const usedCustomerFeatures: string[] = [];
    const iterate = (syntax: Syntax) => {
      if (syntax == undefined) return;
      if (
        syntax.value.type == 'Common Variable' &&
        ((syntax.value as VariableToken).variableType as any) == VariableType.EVENT_VARIABLE &&
        (syntax.value as VariableToken).name == rowData.name
      ) {
        usedCustomerFeatures.push((syntax.value as VariableToken).name);
      }
      syntax.children?.forEach(iterate);
    };
    aiModel.customFeatures.forEach((customFeature) => {
      iterate(customFeature.expression);
    });
    return usedCustomerFeatures;
  };

  private onChangeDescription = (e: React.FormEvent<HTMLInputElement>, rowData: MLOpsModelBaseFeature) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    rowData.description = e.currentTarget.value;
    changeAiModel(aiModel);
  };

  private getDataTypeOptions() {
    return [
      {
        label: LanguageUtil.get({ id: 'common.label.category', defaultMessage: 'Category' }),
        value: DataType.STRING,
      },
      {
        label: LanguageUtil.get({ id: 'common.label.number', defaultMessage: 'Number' }),
        value: DataType.NUMBER,
      },
      {
        label: LanguageUtil.get({ id: 'common.label.boolean', defaultMessage: 'Boolean' }),
        value: DataType.BOOLEAN,
      },
      {
        label: LanguageUtil.get({ id: 'common.label.date', defaultMessage: 'Date' }),
        value: DataType.DATE,
      },
    ];
  }

  private onChangeDataType = (e: FormicaSelectBoxItem, rowData: MLOpsModelBaseFeature) => {
    const {
      aiModel: { aiModel },
      changeAiModel,
    } = this.props;
    rowData.properties.dataType = e.value as DataType;
    changeAiModel(aiModel);
  };
}

interface Props extends ReduxProps, FormicaProps {
  incrementUpdate: () => void;
}

const mapStateToProps = (state: RootState) => ({
  aiModel: state.aiModel as AiModelState,
  isUpdate: state.aiModel.isUpdate,
});
const mapDispatchToProps = {
  changeAiModel,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(AiModelOriginalFeaturesComponent));
